// Small DOM and timing helpers.

export const settings = {
  /** 1 = normal pacing, lower = faster (for presenting). */
  pace: 1,
  sound: true,
};

/** Waits `ms` milliseconds, scaled by the presenter's pace setting. */
export function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms * settings.pace));
}

/** Waits exactly `ms` milliseconds (for sensors and animations, never scaled). */
export function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

type Attrs = Record<string, string | number | boolean | undefined | null>;

/** Creates an element: el('p.note', { title: 'x' }, 'text', child). */
export function el<K extends keyof HTMLElementTagNameMap>(spec: K | `${K}.${string}` | `${K}#${string}`, attrs: Attrs = {}, ...children: Array<Node | string | null | undefined | false>): HTMLElementTagNameMap[K] {
  const [tagAndId, ...classes] = spec.split('.');
  const [tag, id] = tagAndId.split('#');
  const node = document.createElement(tag as K);
  if (id) node.id = id;
  if (classes.length) node.className = classes.join(' ');
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'html') node.innerHTML = String(v);
    else node.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) node.append(c);
  return node;
}

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;

export function clock(d = new Date()): string {
  return d.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
}

export function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

export function longDate(d: Date): string {
  return d.toLocaleDateString('nl-BE', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function pick<T>(arr: T[], avoid?: Set<T>): T {
  const options = avoid ? arr.filter((x) => !avoid.has(x)) : arr;
  const choice = (options.length ? options : arr)[Math.floor(Math.random() * (options.length || arr.length))];
  avoid?.add(choice);
  return choice;
}

/** Reading time for a chat message: a character "types" before it appears. */
export function typingTime(text: string): number {
  return Math.min(2600, 650 + text.length * 26);
}

export const isTouch = () => matchMedia('(pointer: coarse)').matches;
export const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
